import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import SafetyPlanUrduForm from "./SafetyPlanUrduForm";

import {
    fetchSafetyPlanUrdu,
    updateSafetyPlanUrdu
} from "../../../../store/slices/safetyPlansUrduSlice";

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
    const result = {};
    for (const key in obj) {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        result[camelKey] = obj[key];
    }
    return result;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
    const result = {};
    for (const key in obj) {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        result[snakeKey] = obj[key];
    }
    return result;
};

const EditSafetyPlanUrdu = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        patientName: "",
        date: "",
        warningSigns: "",
        internalCoping: "",
        socialDistractions: "",
        peopleForHelp: "",
        professionals: "",
        safeEnvironment: "",
        reasonsForLiving: "",
        additionalNotes: ""
    });

    // Fetch record and map backend snake_case fields to camelCase
    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await dispatch(fetchSafetyPlanUrdu(id)).unwrap();
                const camelData = toCamelCase(response);
                setFormData(camelData);
            } catch (err) {
                Swal.fire("Error", err || "Failed to fetch Safety Plan (Urdu).", "error");
            }
        };

        loadData();
    }, [id, dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Convert formData to snake_case before sending to backend
        const payload = toSnakeCase({
            ...formData,
            caseId,
            patientId
        });

        try {
            await dispatch(updateSafetyPlanUrdu({ case_id: caseId, planData: payload })).unwrap();
            Swal.fire("Updated!", "Safety Plan (Urdu) has been updated successfully.", "success");
            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (err) {
            Swal.fire("Error", err || "Failed to update Safety Plan (Urdu).", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/optional/safety-plan-urdu/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <h5 className="card-header text-center fw-bold">Edit Safety Plan (Urdu)</h5>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <SafetyPlanUrduForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end gap-2">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i>
                                        Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditSafetyPlanUrdu;
